import React, { useState, useRef, useEffect, FormEvent } from 'react';

interface Message {
  sender: 'user' | 'ai' | 'system' | 'error';
  text: string;
  timestamp: Date | string;
  isStreaming?: boolean;
}

interface ChatDisplayProps {
  messages: Message[];
  onSendMessage: (message: string) => void;
  isAIResponding?: boolean;
  onInterrupt?: () => void;
}

const ChatDisplay: React.FC<ChatDisplayProps> = ({
  messages,
  onSendMessage,
  isAIResponding: _isAIResponding = false,
  onInterrupt: _onInterrupt,
}) => {
  const [inputText, setInputText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent double submission
    if (isSubmitting || !inputText.trim()) {
      return;
    }

    const textToSend = inputText.trim();
    setIsSubmitting(true);
    setInputText(''); // Clear input immediately to prevent double send

    try {
      onSendMessage(textToSend);
    } finally {
      // Reset submitting state after a short delay to allow message to be sent
      setTimeout(() => {
        setIsSubmitting(false);
      }, 100);
    }
  };

  const getSenderLabel = (sender: string): string => {
    switch (sender) {
      case 'user':
        return 'You';
      case 'ai':
        return 'AI Interviewer';
      case 'system':
        return 'System';
      default:
        return 'Error';
    }
  };

  const getMessageClasses = (sender: string): string => {
    const baseClasses = 'flex mb-2 min-w-0 w-full';
    switch (sender) {
      case 'user':
        return `${baseClasses} justify-end`;
      case 'ai':
        return `${baseClasses} justify-start`;
      case 'system':
      case 'error':
        return `${baseClasses} justify-center`;
      default:
        return baseClasses;
    }
  };

  const getContentClasses = (sender: string): string => {
    const baseClasses =
      'max-w-[95%] sm:max-w-[85%] min-w-0 px-3 sm:px-4 py-2 sm:py-3 rounded-xl break-words';
    switch (sender) {
      case 'user':
        return `${baseClasses} bg-gradient-to-r from-indigo-500 to-purple-600 text-white`;
      case 'ai':
        return `${baseClasses} bg-indigo-50 text-slate-800 border border-indigo-200`;
      case 'system':
        return `${baseClasses} bg-slate-100 text-slate-500 text-sm border border-slate-200`;
      case 'error':
        return `${baseClasses} bg-red-50 text-red-900`;
      default:
        return baseClasses;
    }
  };

  return (
    <div className="flex flex-col h-full min-w-0">
      <div className="px-3 sm:px-5 py-3 sm:py-4 border-b-2 border-slate-200 bg-slate-50 flex justify-between items-center flex-shrink-0">
        <h3 className="m-0 text-slate-800 text-base sm:text-xl font-semibold truncate">
          Conversation
        </h3>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 sm:p-4 bg-transparent flex flex-col gap-3">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-400 text-center py-10 px-10">
            <p>Start speaking or sharing your screen to begin the interview</p>
          </div>
        ) : (
          messages.map((msg, index) => (
            <div key={index} className={getMessageClasses(msg.sender)}>
              <div
                className={`${getContentClasses(msg.sender)} w-fit max-w-full`}
              >
                <div className="flex justify-between items-center gap-2 mb-1 text-xs opacity-80 min-w-0">
                  <span className="font-semibold truncate">
                    {getSenderLabel(msg.sender)}
                  </span>
                  <span className="flex-shrink-0">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="leading-relaxed break-words text-left">
                  {msg.text}
                  {msg.isStreaming && (
                    <span className="inline-block ml-0.5 animate-pulse">▋</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex p-3 sm:p-4 border-t border-indigo-300/30 bg-slate-900/80 gap-2 flex-shrink-0 min-w-0"
      >
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            // Prevent form submission on Enter if already submitting
            if (e.key === 'Enter' && isSubmitting) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          placeholder="Type a message or use voice..."
          className="flex-1 min-w-0 px-3 py-2 sm:py-3 border-2 border-slate-200 bg-white text-slate-800 rounded-lg text-sm outline-none transition-colors placeholder:text-slate-400 focus:border-indigo-500 focus:ring-3 focus:ring-indigo-100"
          disabled={isSubmitting}
        />
        <button
          type="submit"
          disabled={isSubmitting || !inputText.trim()}
          className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-none rounded-lg font-semibold cursor-pointer transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex-shrink-0"
        >
          {isSubmitting ? '...' : 'Send'}
        </button>
      </form>
    </div>
  );
};

export default ChatDisplay;
